import { useCallback, useState } from 'react';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { setCookie } from 'nookies';
import { Wallet, Globe, HelpCircle, ExternalLink } from 'lucide-react';
import { useModal } from 'connectkit';
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@gu-corp/gu-ui';

import { ThemeProvider } from '~/components/theme-provider';

const languageOptions = [
  { value: 'en', labelKey: 'english' },
  { value: 'ja', labelKey: 'japanese' },
  { value: 'ar', labelKey: 'arabic' },
  { value: 'de', labelKey: 'german' },
  { value: 'es', labelKey: 'spanish' },
  { value: 'fr', labelKey: 'french' },
  { value: 'hi', labelKey: 'hindi' },
  { value: 'id', labelKey: 'indonesian' },
  { value: 'it', labelKey: 'italian' },
  { value: 'ko', labelKey: 'korean' },
  { value: 'pt', labelKey: 'portuguese' },
  { value: 'ru', labelKey: 'russian' },
  { value: 'th', labelKey: 'thai' },
  { value: 'tr', labelKey: 'turkish' },
  { value: 'vi', labelKey: 'vietnamese' },
  { value: 'zh-CN', labelKey: 'chinese_simplified' },
  { value: 'zh-TW', labelKey: 'chinese_traditional' },
] as const;

const walletLinks = {
  gu: 'https://chrome.google.com/webstore/detail/guwalletextension/ophkaoefkoofkkjlmfgnjmjnnpfmmfgc',
  lunascape: 'https://www.lunascape.org/',
};

const WalletConnect = () => {
  const router = useRouter();
  const { t } = useTranslation('common');
  const { setOpen } = useModal();
  const [helpDialogOpen, setHelpDialogOpen] = useState(false);

  const onOpenWalletConnectorDialog = useCallback(() => {
    setOpen(true);
  }, [setOpen]);

  const onLanguageChange = useCallback(
    (selectedLanguage: string) => {
      setCookie(null, 'NEXT_LOCALE', selectedLanguage);
      window.location.href = '/' + selectedLanguage + router.asPath;
    },
    [router],
  );

  return (
    <ThemeProvider>
      <div
        className="min-h-screen flex flex-col bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800"
        data-testid="wallet-connect-page"
      >
        {/* Header */}
        <header className="flex items-center justify-between px-4 h-12 bg-background/80 backdrop-blur-sm border-b border-border">
          <div />
          <div className="flex items-center gap-1.5">
            <img className="h-5" src="/images/new_joc_icon.svg" alt="JOC" />
            <span className="text-sm font-medium text-foreground">JOC Club</span>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                data-testid="language-selector"
              >
                <Globe className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className="bg-popover text-popover-foreground max-h-80 overflow-y-auto"
            >
              {languageOptions.map((option) => (
                <DropdownMenuItem key={option.value} onClick={() => onLanguageChange(option.value)}>
                  {t(option.labelKey)}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </header>

        {/* Main Content */}
        <div className="w-full flex items-center justify-center flex-1 p-4">
          <div className="w-full max-w-md">
            <Card className="animate-in fade-in slide-in-from-bottom-4 duration-500">
              <CardHeader className="text-center pb-4">
                <div className="mx-auto w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                  <img className="h-10" src="/images/new_joc_icon.svg" alt="JOC" />
                </div>
                <CardTitle className="text-xl">{t('wallet_connect.header')}</CardTitle>
                <CardDescription>{t('wallet_connect.description')}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <Button
                  className="w-full"
                  size="lg"
                  data-testid="connect-button"
                  onClick={onOpenWalletConnectorDialog}
                >
                  <Wallet className="w-4 h-4 mr-2" />
                  {t('wallet_connect.connect')}
                </Button>
                <button
                  onClick={() => setHelpDialogOpen(true)}
                  className="w-full flex items-center justify-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
                  data-testid="help-trigger"
                >
                  <HelpCircle className="w-4 h-4" />
                  {t('wallet_connect.what_is_wallet')}
                </button>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Help Dialog */}
        <Dialog open={helpDialogOpen} onOpenChange={setHelpDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>{t('wallet_connect.wallet_explanation_title')}</DialogTitle>
              <DialogDescription>{t('wallet_connect.wallet_explanation')}</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <h4 className="font-medium mb-3">{t('wallet_connect.no_wallet_title')}</h4>
                <p className="text-sm text-muted-foreground mb-4">
                  {t('wallet_connect.no_wallet_description')}
                </p>
                <div className="space-y-2">
                  {/* G.U.Wallet */}
                  <a
                    href={walletLinks.gu}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-between p-3 rounded-lg border hover:bg-muted/50 transition-colors"
                  >
                    <div>
                      <p className="font-medium">{t('wallet_connect.wallet_gu')}</p>
                      <p className="text-sm text-muted-foreground">
                        {t('wallet_connect.wallet_gu_desc')}
                      </p>
                    </div>
                    <ExternalLink className="w-4 h-4 text-muted-foreground" />
                  </a>
                  {/* Lunascape */}
                  <a
                    href={walletLinks.lunascape}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-between p-3 rounded-lg border hover:bg-muted/50 transition-colors"
                  >
                    <div>
                      <p className="font-medium">{t('wallet_connect.wallet_lunascape')}</p>
                      <p className="text-sm text-muted-foreground">
                        {t('wallet_connect.wallet_lunascape_desc')}
                      </p>
                    </div>
                    <ExternalLink className="w-4 h-4 text-muted-foreground" />
                  </a>
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </ThemeProvider>
  );
};

export default WalletConnect;
